import React, { useEffect, useLayoutEffect, Suspense, lazy, useState } from 'react';
import { Route } from 'react-router-dom';
import { Admin, Resource } from 'react-admin';
import { VerifyContact, TOTPSetup, Authenticator } from 'aws-amplify-react';
import Auth from '@aws-amplify/auth';
import { Hub, HubCapsule } from '@aws-amplify/core';
import { datadogLogs } from '@datadog/browser-logs';
// import Analytics from '@aws-amplify/analytics';
import { ProfileProvider } from 'app/providers/ProfileProvider';

import { createLabel, createReturnLabel } from 'pages/calculator';
import { calculator } from 'pages/newCalculator';
import groupedOrders from 'pages/orders/components/group/GroupedOrders';
import groupCalculator from 'pages/orders/components/group/GroupedCalculator';
import groupedShipments from 'pages/shipments/components/group/GroupedShipments';
import { help } from 'pages/help';
import privacyPolicy from 'pages/privacyPolicy';
import inventorySettings from 'pages/inventorySettings';
import totpSettings from 'pages/totpSettings';
import tiktokConnect from 'pages/sellingChannels/components/TikTokConnect';

import print from 'pages/print';
import notificationPreferences from 'pages/notificationPreferences';
import profile from 'pages/profile';
import {
  orders,
  sellingChannels,
  shipmentAccounts,
  shipments,
  schedulePickup,
  pickupHistory,
  shippingPresets,
  warehousePresets,
  billing,
  endOfDay,
  addressBook,
  inventory,
  mailTemplate,
} from './pages';
import {
  ConfirmationWasSent,
  OAuthRedirect,
  RecoveryWasSent,
  SignIn,
  SignUp,
  ConfirmSignIn,
  Maintenance,
  RequireNewPassword,
} from './pages/auth';
import CustomResource from 'components/common/CustomResource';
import Layout from './components/layout/Layout';
import CalculatorButton from 'components/buttons/CalculatorButton';
import { UPSClaimForm, USPSClaimForm } from './pages/claims';
import { RewardsPage, RewardsLevelsPage } from './pages/rewards';
import SeoMetadata from 'components/common/SeoMetadata';

import data from './services/data';
import reducers from 'store/reducer';
import { isMobile } from 'services/helpers/isMobile';
import { signOut } from 'services/helpers/signOut';
import { getDomesticCountryCode } from 'services/helpers/countryCode';
import { config } from './services/auth';

import { LoadingSpinner } from 'components/common';
import {
  authHistory,
  CONFIRM_SIGN_UP_ROUTE,
  PUBLIC_AUTH_ENTRY_STATE_BY_ROUTE,
  PUBLIC_AUTH_ROUTES,
  RESET_PASSWORD_ROUTE,
  SIGN_IN_ROUTE,
} from 'components/layout/AuthLayout';
import { NotificationsProvider } from 'app/providers/NotificationsProvider';

const GroupSettings = lazy(() => import('pages/groupingAliases/GroupSettings'));
const HomePage = lazy(() => import('pages/home/HomePage'));
const MobileView = lazy(() => import('pages/mobile/MobileView'));
const MaintenancePage = lazy(() => import('pages/maintenance'));

Auth.configure(config);

// Analytics.configure(analyticsConfig)

// Analytics.autoTrack('session', {
//   enable: true
// });

// Analytics.autoTrack('pageView', {
//   enable: true,
//   type: 'SPA'
// });

// Analytics.autoTrack('event', {
//   enable: true
// });

const handleUserSubscription = ({ payload }: HubCapsule) => {
  if (payload.event === 'signOut' && !PUBLIC_AUTH_ROUTES.includes(window.location.pathname)) {
    window.location.href = SIGN_IN_ROUTE;
  }
};

// Hide the initial HTML loader when React takes over
const hideInitialLoader = () => {
  const initialLoader = document.getElementById('initial-loader');
  if (initialLoader) {
    initialLoader.style.display = 'none';
  }
  // Mark body as app loaded
  document.body.classList.add('app-loaded');
};

const App = React.memo(() => {
  // useEffect(() => {
  //   Analytics.record({
  //     name: "initApp"
  //   })
  // }, [])

  useEffect(() => {
    Hub.listen('auth', handleUserSubscription);
    return () => {
      Hub.remove('auth', handleUserSubscription);
    };
  }, []);

  useEffect(() => {
    // Hide the initial loader when the main app component mounts
    hideInitialLoader();
  }, []);

  const domesticCountryCode = getDomesticCountryCode();

  useEffect(() => {
    datadogLogs.init({
      clientToken: `${process.env.REACT_APP_DATADOG_CLIENT_TOKEN}`,
      site: process.env.REACT_APP_DATADOG_SITE,
      env: process.env.REACT_APP_AWS_ENV,
      forwardErrorsToLogs: false,
      sampleRate: 100,
    });
  }, []);

  return (
    <Suspense fallback={<LoadingSpinner message="Loading application..." fullScreen />}>
      <Admin
        dataProvider={data}
        layout={Layout}
        history={authHistory}
        customReducers={reducers}
        customRoutes={[
          <Route key="profile" path="/profile" component={profile.edit} />,
          <Route key="privacy-policy" path="/privacy-policy" component={privacyPolicy.show} />,
          <Route key="print" path="/print" component={print.show} />,
          <Route
            key="notificationPreferences"
            path="/notificationPreferences"
            component={notificationPreferences.show}
          />,
          <Route
            key="inventorySettings"
            path="/inventory-settings"
            component={inventorySettings.show}
          />,
          <Route key="totpSettings" path="/totp-settings" component={totpSettings.show} />,

          <Route key="ship-order" path="/orders/:id/calculate" component={createLabel} />,
          <Route key="return-label" path="/shipments/:id/return" component={createReturnLabel} />,
          <Route key="help" path="/help/:id*" component={help} />,
          ...(domesticCountryCode === 'US'
            ? [<Route key="endOfDay" path="/shipments/end-of-day" component={endOfDay.list} />]
            : []),
          <Route key="pickupHistory" path="/schedule/history" component={pickupHistory.list} />,
          <Route key="groupedOrders" path="/orders/groups/:id/show" component={groupedOrders} />,
          <Route
            key="groupPrices"
            path="/orders/groups/:id/calculate"
            component={groupCalculator}
          />,

          <Route
            key="groupedShipments"
            path="/shipments/groups/:id/show"
            component={groupedShipments}
          />,
          <Route key="tiktokConnect" path="/connect/tiktok" component={tiktokConnect} />,
          <Route key="UPSClaim" path="/claim/ups/:id" component={UPSClaimForm} />,
          <Route key="USPSClaim" path="/claim/usps/:id" component={USPSClaimForm} />,
          <Route key="rewards" path="/rewards" component={RewardsPage} />,
          <Route key="rewardsLevels" path="/rewards-levels" component={RewardsLevelsPage} />,
        ]}>
        <Resource
          name="home"
          options={{ label: 'Home', menu: 'primary', order: 1 }}
          list={HomePage}
        />
        <Resource name="orders" options={{ label: 'Orders', menu: 'primary' }} {...orders} />
        <Resource name="shipments" options={{ label: 'Shipped', menu: 'primary' }} {...shipments} />
        <Resource
          name="inventory"
          options={{ label: 'Inventory', menu: 'primary' }}
          {...inventory}
        />

        <Resource
          name="schedule"
          options={{ label: 'Schedule Pickup', menu: 'primary' }}
          {...schedulePickup}
        />

        <Resource name="contacts" options={{ label: 'Contacts', menu: 'user' }} {...addressBook} />
        <CustomResource
          name="calculator"
          path="/calculator"
          options={{ label: <CalculatorButton />, menu: 'actions' }}
          component={calculator}
        />
        <Resource
          name="sellingChannels"
          options={{ label: 'Selling Channels', menu: 'connections' }}
          {...sellingChannels}
        />
        <Resource
          name="shipmentAccounts"
          options={{ label: 'Shipping Carriers', menu: 'connections' }}
          {...shipmentAccounts}
        />
        <Resource
          name="shippingPresets"
          options={{ label: 'Package Presets', menu: 'settings' }}
          {...shippingPresets}
        />
        <Resource
          name="mailTemplate"
          options={{ label: 'Email', menu: 'settings' }}
          {...mailTemplate}
        />
        <Resource
          name="warehousePresets"
          options={{ label: 'Ship From Addresses', menu: 'settings' }}
          {...warehousePresets}
        />
        <Resource
          name="grouping"
          options={{ label: 'Group', menu: 'settings' }}
          list={GroupSettings}
        />
        <Resource name="billing" {...billing} />
        <Resource name="shippingPreset" />
        <Resource name="singlePickupList" />
        <Resource name="automaticPickupList" />
        <Resource name="packageTypes" />
        <Resource name="rates" />
        <Resource name="countries" />
        <Resource name="domesticCountries" />
        <Resource name="states" />
        <Resource name="labelInfo" />
        <Resource name="stores" />
        <Resource name="orderStatuses" />
        <Resource name="shipmentStatuses" />
        <Resource name="shipmentTypes" />
        <Resource name="singlePickupStatuses" />
        <Resource name="orderItems" />
        <Resource name="deliveryProviders" />
        <Resource name="deliveryProvidersByoa" />
        <Resource name="ebayChannel" />
        <Resource name="profile" />
        <Resource name="billingBalance" />
        <Resource name="billingStats" />
        <Resource name="paymentOptions" />
        <Resource name="additionalDocument" />
        <Resource name="tableSettings" />
        <Resource name="rowsPerPageSettings" />
        <Resource name="returnShipments" />
        <Resource name="paymentStatuses" />
        <Resource name="scanForms" />
        <Resource name="promoteShipmentAccount" />
        <Resource name="pickupHistory" />
        <Resource name="scanFormsHistory" />
        <Resource name="scanFormsShipments" />
        <Resource name="scanFormsWarehouses" />
        <Resource name="scanFormsProviders" />
        <Resource name="printScanForms" />
        <Resource name="groups" />
        <Resource name="groupedOrdersIds" />
        <Resource name="groupPrices" />
        <Resource name="groupSettings" />
        <Resource name="groupLabels" />
        <Resource name="groupShipments" />
        <Resource name="groupShipmentsLabels" />
        <Resource name="groupOrdersInfo" />
        <Resource name="groupShipmentsInfo" />
        <Resource name="deliveryServices" />
        <Resource name="groupedShipmentsIds" />
        <Resource name="userServices" />
        <Resource name="shipmentMethodGroups" />
        <Resource name="skuGroups" />
        <Resource name="pickupProviders" />
        <Route path="/redirect" component={OAuthRedirect} />
      </Admin>
    </Suspense>
  );
});

const AuthComponentWrapper = ({
  component,
  ...props
}: { component?: (props: any) => JSX.Element | null } & any) => {
  const Component = component;
  return <Component {...props} />;
};

const MobileViewWrapper = () => {
  return <MobileView />;
};

const AUTHENTICATED_HOME_ROUTE = '/home';

const getPathWithSearch = (pathname: string, search = '') => `${pathname}${search || ''}`;

const getLocationPath = (location: { pathname: string; search?: string }) =>
  getPathWithSearch(location.pathname, location.search);

const getAuthStatePathname = (path: string) => path.split('?')[0] || SIGN_IN_ROUTE;

const isConfirmSignUpRequest = (pathname: string, search = '') => {
  if (pathname !== CONFIRM_SIGN_UP_ROUTE) {
    return false;
  }

  const urlParams = new URLSearchParams(search);

  return !!urlParams.get('username') && !!urlParams.get('code');
};

const getSignedInPath = (pathname: string, search = '') => {
  return PUBLIC_AUTH_ROUTES.includes(pathname)
    ? AUTHENTICATED_HOME_ROUTE
    : getPathWithSearch(pathname, search);
};

const getSignedOutPath = (pathname: string, search = '') => {
  return PUBLIC_AUTH_ROUTES.includes(pathname)
    ? getPathWithSearch(pathname, search)
    : SIGN_IN_ROUTE;
};

const getPublicEntryAuthState = (pathname: string) => {
  return PUBLIC_AUTH_ENTRY_STATE_BY_ROUTE[pathname] || 'signIn';
};

const isMobilePublicRouteException = () => {
  if (!isMobile()) {
    return false;
  }

  return [CONFIRM_SIGN_UP_ROUTE, RESET_PASSWORD_ROUTE].includes(window.location.pathname);
};

const API_URL = process.env.REACT_APP_DATA_PROVIDER_URL;

const AuthWrapper = () => {
  const [authBootstrapState, setAuthBootstrapState] = useState<
    'checking' | 'authenticated' | 'signedOut'
  >('checking');
  const [authState, setAuthState] = useState<string | null>();
  const [authPathname, setAuthPathname] = useState<string>(() => authHistory.location.pathname);

  const handleAuthStateChange = (state: string) => {
    setAuthState(state);

    if (state === 'signedIn') {
      const nextPathname = getSignedInPath(
        authHistory.location.pathname,
        authHistory.location.search
      );

      setAuthBootstrapState('authenticated');
      setAuthPathname(getAuthStatePathname(nextPathname));

      if (getLocationPath(authHistory.location) !== nextPathname) {
        authHistory.replace(nextPathname);
      }

      return;
    }

    setAuthBootstrapState('signedOut');
  };

  useLayoutEffect(() => {
    let isMounted = true;

    const bootstrapAuthState = async () => {
      if (isConfirmSignUpRequest(authHistory.location.pathname, authHistory.location.search)) {
        await signOut(null);

        if (!isMounted) {
          return;
        }

        setAuthBootstrapState('signedOut');
        setAuthPathname(authHistory.location.pathname);
        return;
      }

      try {
        await Auth.currentAuthenticatedUser();

        if (!isMounted) {
          return;
        }

        const nextPathname = getSignedInPath(
          authHistory.location.pathname,
          authHistory.location.search
        );

        setAuthState('signedIn');
        setAuthBootstrapState('authenticated');
        setAuthPathname(getAuthStatePathname(nextPathname));

        if (getLocationPath(authHistory.location) !== nextPathname) {
          authHistory.replace(nextPathname);
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }

        const nextPathname = getSignedOutPath(
          authHistory.location.pathname,
          authHistory.location.search
        );

        setAuthBootstrapState('signedOut');
        setAuthPathname(getAuthStatePathname(nextPathname));

        if (getLocationPath(authHistory.location) !== nextPathname) {
          authHistory.replace(nextPathname);
        }
      }
    };

    bootstrapAuthState();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    return authHistory.listen((location) => {
      if (authBootstrapState === 'checking') {
        setAuthPathname(location.pathname);
        return;
      }

      const nextPathname =
        authBootstrapState === 'authenticated'
          ? getSignedInPath(location.pathname, location.search)
          : getSignedOutPath(location.pathname, location.search);

      setAuthPathname(getAuthStatePathname(nextPathname));

      if (getLocationPath(location) !== nextPathname) {
        authHistory.replace(nextPathname);
      }
    });
  }, [authBootstrapState]);

  useEffect(() => {
    // Hide the initial loader when auth wrapper mounts
    hideInitialLoader();
  }, []);

  if (authBootstrapState === 'checking') {
    return <LoadingSpinner message="Loading..." fullScreen />;
  }

  if (authState === 'signedIn') {
    return (
      <ProfileProvider>
        <NotificationsProvider>
          <App />
        </NotificationsProvider>
      </ProfileProvider>
    );
  }

  return (
    <Suspense fallback={<LoadingSpinner message="Loading..." fullScreen />}>
      <Authenticator
        key={getPublicEntryAuthState(authPathname)}
        hideDefault={true}
        authState={getPublicEntryAuthState(authPathname)}
        onStateChange={handleAuthStateChange}>
        <AuthComponentWrapper component={SignIn} />
        <AuthComponentWrapper component={Maintenance} />
        <AuthComponentWrapper component={RecoveryWasSent} />
        <AuthComponentWrapper component={SignUp} />
        <AuthComponentWrapper component={ConfirmationWasSent} />
        <AuthComponentWrapper component={ConfirmSignIn} />
        <AuthComponentWrapper component={RequireNewPassword} />
        <VerifyContact />
        <TOTPSetup />
      </Authenticator>
    </Suspense>
  );
};

const AppWrapper = () => {
  const [isMaintenance, setIsMaintenance] = useState<boolean | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [metadataPathname, setMetadataPathname] = useState(() => authHistory.location.pathname);

  const handleMaintenanceMode = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/actuator/health`, { method: 'GET' });

      const text = await response.text();
      const body = JSON.parse(text);

      if (body.status === 'UP') {
        setIsMaintenance(false);
      } else {
        setIsMaintenance(true);
      }
    } catch (error) {
      setIsMaintenance(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleMaintenanceMode();
  }, []);

  useEffect(() => {
    setMetadataPathname(authHistory.location.pathname);

    return authHistory.listen((location) => {
      setMetadataPathname(location.pathname);
    });
  }, []);

  useEffect(() => {
    // Hide the initial loader when app wrapper mounts
    if (!loading && isMaintenance !== null) {
      hideInitialLoader();
    }
  }, [loading, isMaintenance]);

  if (loading) {
    return (
      <>
        <SeoMetadata pathname={metadataPathname} />
        <LoadingSpinner message="Checking system status..." fullScreen />
      </>
    );
  }

  if (isMaintenance === null) {
    return (
      <>
        <SeoMetadata pathname={metadataPathname} />
        <LoadingSpinner message="Initializing..." fullScreen />
      </>
    );
  }

  if (isMaintenance) {
    return (
      <>
        <SeoMetadata pathname={metadataPathname} />
        <Suspense fallback={<LoadingSpinner message="Loading maintenance page..." />}>
          <MaintenancePage />
        </Suspense>
      </>
    );
  }

  return (
    <>
      <SeoMetadata pathname={metadataPathname} />
      {isMobile() && !isMobilePublicRouteException() ? (
        <Suspense fallback={<LoadingSpinner message="Loading mobile view..." fullScreen />}>
          <MobileViewWrapper />
        </Suspense>
      ) : (
        <AuthWrapper />
      )}
    </>
  );
};

export default AppWrapper;
